import { cn } from '@/lib/utils';
import type { SnapshotCapability } from '@/sections/customerManagement/types';

interface CapabilityAuthorshipProps {
  capabilities: SnapshotCapability[];
}

export function CapabilityAuthorship({
  capabilities,
}: CapabilityAuthorshipProps) {
  if (capabilities.length === 0) return null;

  const maxLines = Math.max(...capabilities.map(c => c.lines));
  const primaryCaps = capabilities.filter(c => c.role === 'primary');
  const secondaryCaps = capabilities.filter(c => c.role === 'secondary');
  const totalLines = capabilities.reduce((sum, c) => sum + c.lines, 0);

  return (
    <div className="space-y-3">
      {primaryCaps.length > 0 && (
        <CapabilitySection
          label="Primary Author"
          caps={primaryCaps}
          maxLines={maxLines}
        />
      )}
      {secondaryCaps.length > 0 && (
        <CapabilitySection
          label="Contributor"
          caps={secondaryCaps}
          maxLines={maxLines}
        />
      )}
      <div className="border-chariot-border flex gap-6 border-t pt-3 text-xs">
        <span className="text-chariot-text-secondary">
          <strong className="text-chariot-text-primary font-semibold">
            {capabilities.length}
          </strong>{' '}
          {capabilities.length === 1 ? 'capability' : 'capabilities'}
        </span>
        <span className="text-chariot-text-secondary">
          <strong className="text-chariot-text-primary font-semibold">
            {primaryCaps.length}
          </strong>{' '}
          primary
        </span>
        <span className="text-chariot-text-secondary">
          <strong className="text-chariot-text-primary font-semibold">
            {secondaryCaps.length}
          </strong>{' '}
          contributor
        </span>
        <span className="text-chariot-text-secondary">
          <strong className="text-chariot-text-primary font-semibold">
            {totalLines.toLocaleString()}
          </strong>{' '}
          total lines
        </span>
      </div>
    </div>
  );
}

function CapabilitySection({
  label,
  caps,
  maxLines,
}: {
  label: string;
  caps: SnapshotCapability[];
  maxLines: number;
}) {
  return (
    <div>
      <p className="text-chariot-text-muted mb-1.5 text-[0.6875rem] font-medium tracking-wider uppercase">
        {label}
      </p>
      <div className="space-y-2">
        {caps.map(cap => (
          <CapabilityRow key={cap.name} cap={cap} maxLines={maxLines} />
        ))}
      </div>
    </div>
  );
}

function CapabilityRow({
  cap,
  maxLines,
}: {
  cap: SnapshotCapability;
  maxLines: number;
}) {
  const pct = maxLines > 0 ? (cap.lines / maxLines) * 100 : 0;

  return (
    <div className="bg-chariot-surface-secondary space-y-1 rounded-md p-2">
      <div className="flex items-center gap-3">
        <span className="text-chariot-text-primary flex-1 font-mono text-sm font-medium">
          {cap.name}
        </span>
        <span
          className={cn(
            'rounded-full border px-2 py-0.5 text-[0.625rem] font-semibold tracking-wider uppercase',
            cap.role === 'primary'
              ? 'border-indigo-500/25 bg-indigo-500/10 text-indigo-400'
              : 'border-slate-400/15 bg-slate-400/10 text-slate-400'
          )}
        >
          {cap.role}
        </span>
        <span className="text-chariot-text-muted text-xs tabular-nums">
          {cap.lines.toLocaleString()} lines
        </span>
      </div>
      <div className="bg-chariot-surface-primary h-[3px] rounded-full">
        <div
          className={cn(
            'h-full rounded-full',
            cap.role === 'primary'
              ? 'bg-indigo-500 opacity-50'
              : 'bg-slate-400 opacity-35'
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
